import { Link } from 'react-router-dom';
import {
  Modulo, alcanca, moduloPorChave, modulosDentroDe,
} from '../modulos';
import { useSessao } from '../sessao';
import { Icone, iconeDoModulo } from '../Icone';

/**
 * A porta dos cadastros — um item no menu, os cadastros atrás dele.
 *
 * ── Por que esta tela existe ─────────────────────────────────────────
 *
 * «O cadastro de itens está ocupando muito do menu lateral. Acredito
 *  que deva ter um local centralizado para cadastrar coisas, como
 *  cliente, fornecedores de calibração, locais de armazenamento»
 * (Humberto, 08/09/2026).
 *
 * O menu lateral é um MAPA: responde «onde fica o trabalho de hoje?».
 * Um cadastro não é trabalho de hoje — é o que se abre uma vez para
 * criar a prateleira, o fornecedor, o cliente novo, e depois se usa de
 * dentro de outras telas sem voltar aqui. Cada cadastro que ganhava
 * item próprio custava uma linha do menu (que tem teto, `33.1`) para
 * um destino que se abre uma vez por mês.
 *
 * ── Como ela se mantém ───────────────────────────────────────────────
 *
 * Os cartões não são lista escrita à mão: são os módulos do registro
 * que declaram `dentroDe: 'cadastros'`. Um cadastro novo entra aqui
 * declarando isso em `modulos.ts`, e só isso — o menu não cresce, a
 * porta cresce. E a porta mostra o que a pessoa ALCANÇA, pela mesma
 * regra do menu (`alcanca`): um cartão que abre para receber recusa é
 * pior do que cartão nenhum.
 *
 * A segunda lista, «que moram em outro lugar», existe porque a
 * pergunta «onde cadastro X?» não para nos três cartões: normas,
 * categorias, equipes e usuários também são cadastros, e cada um mora
 * onde mora por uma razão escrita no registro. Aqui só se aponta o
 * caminho, para ninguém procurar e não achar.
 */

/**
 * Cadastros que existem, mas moram em outra parte do sistema — com a
 * razão em meia linha. As chaves são as do registro; o título e a rota
 * vêm de lá, para esta lista não ficar velha quando um deles mudar de
 * nome ou de endereço.
 */
const MORAM_EM_OUTRO_LUGAR: { chave: string; onde: string }[] = [
  { chave: 'campo', onde: 'Equipes e veículos ficam em Campo, com o diário — quem cadastra é quem programa o dia.' },
  { chave: 'equipamentos', onde: 'O parque, com calibrações e verificações, é registro do SGQ.' },
  { chave: 'normas', onde: 'Normas, edições e métodos são registro da qualidade, no SGQ.' },
  { chave: 'itens_medicao', onde: 'O catálogo do que a casa vende abre de dentro da Medição.' },
  { chave: 'categorias_equipamento', onde: 'O que define uma balança é configuração de administrador: engrenagem ⚙, ao lado do seu nome.' },
  { chave: 'usuarios', onde: 'Contas, perfis e permissões: engrenagem ⚙, ao lado do seu nome.' },
];

export function Cadastros() {
  const { eu } = useSessao();
  const permissoes = eu?.permissoes ?? [];

  const cartoes = modulosDentroDe('cadastros').filter((m) => alcanca(m, permissoes));
  const outros = MORAM_EM_OUTRO_LUGAR
    .map((o) => ({ ...o, modulo: moduloPorChave(o.chave) }))
    .filter((o): o is typeof o & { modulo: Modulo } => !!o.modulo && !!o.modulo.rota
      && alcanca(o.modulo, permissoes));

  return (
    <div>
      <div className="linha-topo">
        <div className="cresce">
          <h1>Cadastros</h1>
          <p className="sub">
            O que se cadastra uma vez e o resto do sistema usa: cliente,
            obra, fornecedor, lugar de guardar amostra
          </p>
        </div>
      </div>

      {cartoes.length === 0 ? (
        <p className="vazio">
          O seu perfil não alcança nenhum cadastro. Se precisa cadastrar
          alguma coisa, peça a permissão «cadastro» a quem administra os
          acessos.
        </p>
      ) : (
        <div className="cadastros-grade">
          {cartoes.map((m) => (
            <Link key={m.chave} to={m.rota as string} className="cadastro-cartao">
              <span className="ic" aria-hidden="true"><Icone nome={iconeDoModulo(m.chave)} /></span>
              <strong>{m.titulo}</strong>
              {m.resumo && <span className="sub">{m.resumo}</span>}
            </Link>
          ))}
        </div>
      )}

      {outros.length > 0 && (
        <div className="cartao plano" style={{ marginTop: '1.2rem' }}>
          <div className="cartao-cabeca">
            <h2>Cadastros que moram em outro lugar</h2>
          </div>
          <p className="sub" style={{ marginTop: '-.3rem' }}>
            Também são cadastros; ficam onde o trabalho deles acontece.
          </p>
          <ul className="cadastros-outros">
            {outros.map((o) => (
              <li key={o.chave}>
                <Link to={o.modulo.rota as string}>
                  <span className="ic" aria-hidden="true">{o.modulo.ic}</span>
                  {o.modulo.titulo}
                </Link>
                <span className="sub">{o.onde}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
